import { format as formatDate } from "date-fns";
import { ArrowRight, X } from "lucide-react";
import { type ComponentProps, Fragment, useEffect, useId, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, type To, useNavigate } from "react-router-dom";

import { NotificationActionButton } from "./NotificationActionButton";
import { getChangeFields, resolveActorName, translateActorName } from "./utils";
import { useIntegrationNames } from "../hooks/useIntegrationNames";
import { useNotificationRouteAccess } from "../hooks/useNotificationRouteAccess";
import { getNotificationIcon } from "../notification-icon";
import type { DetailField, NotificationData, NotificationDetailActions, NotificationSeverity } from "../types";
import {
  getNotificationLinks,
  getNotificationSeverityStyles,
  getTranslationKey,
  getTranslationValues,
} from "../utils";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { cn } from "@/lib/utils/cn";
import { formatDateForTooltip, getDateFnsLocale, parseLocalDateOnly } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

const SPLIT_VIEW_QUERY = "(min-width: 64rem)";

const EDITABLE_SELECTOR = "input, textarea, select";

function isEditableTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && (target.isContentEditable || target.closest(EDITABLE_SELECTOR) !== null);
}

const SEVERITY_BADGE_VARIANT: Record<NotificationSeverity, ComponentProps<typeof Badge>["variant"]> = {
  Info: "info",
  Warning: "warning",
  Error: "error",
};

type NotificationDetailProps = {
  notification: NotificationData;
  closeHref: To;
  actions?: NotificationDetailActions;
};

export function NotificationDetail({ notification, closeHref, actions }: NotificationDetailProps) {
  const { t, i18n } = useTranslation();
  const routeAccess = useNotificationRouteAccess();
  const navigate = useNavigate();
  const { format } = useValueFormatter();
  const titleId = useId();
  const articleRef = useRef<HTMLElement>(null);
  const integrationNames = useIntegrationNames();
  const links = getNotificationLinks(notification, routeAccess, integrationNames);
  const severityStyles = getNotificationSeverityStyles(notification.severity);
  const DomainIcon = getNotificationIcon(notification.eventType);

  const fields: DetailField[] = [
    {
      label: t("notifications.detail.fields.actor"),
      value: translateActorName(resolveActorName(notification.actorName), t),
    },
    {
      label: t("notifications.detail.fields.createdAt"),
      value: formatDateForTooltip(notification.createdAt, i18n.language),
    },
    ...getChangeFields(notification, {
      t,
      context: routeAccess.context,
      links,
      formatMonth: (value) => {
        const month = parseLocalDateOnly(value);
        return month ? formatDate(month, "LLLL yyyy", { locale: getDateFnsLocale(i18n.language) }) : value;
      },
      formatCurrency: (value) => format(value, "currency"),
    }),
  ];

  useEffect(() => {
    if (window.matchMedia(SPLIT_VIEW_QUERY).matches) return;
    articleRef.current?.focus();
  }, [notification.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || isEditableTarget(event.target)) return;
      navigate(closeHref);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navigate, closeHref]);

  return (
    <article
      ref={articleRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      data-slot="pane"
      className="flex h-full flex-col gap-6 overflow-y-auto rounded-lg border p-5 focus-visible:outline-none"
    >
      <header className="flex items-start gap-3">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full",
            severityStyles.background,
            severityStyles.icon,
          )}
        >
          <DomainIcon className="size-5" aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={SEVERITY_BADGE_VARIANT[notification.severity]}>
              {t(`notifications.severity.${notification.severity}`)}
            </Badge>
            {notification.archivedAt !== null && (
              <Badge variant="archive">{t("notifications.status.archived")}</Badge>
            )}
          </div>
          <h2 id={titleId} className="wrap-break-word text-base leading-snug font-normal">
            <Trans
              i18nKey={getTranslationKey(notification)}
              values={getTranslationValues(notification, t)}
              components={{ strong: <strong className="font-medium" /> }}
            />
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions && (
            <>
              <NotificationActionButton
                action={actions.readAction}
                onClick={() => actions.onAction(actions.readAction)}
                className="pointer-coarse:size-11"
              />
              <NotificationActionButton
                action={actions.archiveAction}
                onClick={() => actions.onAction(actions.archiveAction)}
                className="pointer-coarse:size-11"
              />
            </>
          )}
          <Button asChild variant="ghost" size="icon-sm" aria-label={t("notifications.detail.close")}>
            <Link to={closeHref}>
              <X aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </header>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-3 text-sm">
        {fields.map((field) => (
          <Fragment key={field.label}>
            <dt className="text-muted-foreground">{field.label}</dt>
            <dd className="wrap-break-word">
              {field.to ? (
                <Link to={field.to} className="text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none">
                  {field.value}
                </Link>
              ) : (
                field.value
              )}
            </dd>
          </Fragment>
        ))}
      </dl>

      {links.primary && (
        <Button asChild variant="outline" className="self-start">
          <Link to={links.primary.to}>
            {t(links.primary.labelKey)}
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      )}
    </article>
  );
}
